import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { useLightbox } from "./LightboxContext";

const COVER = "/calendar/00-cover.jpg";

const months = [
  {
    num: "01",
    name: "January",
    player: "Tracy McGrady",
    team: "Orlando Magic",
    accent: "#0077C0",
    img: "/calendar/01-jan.jpg",
  },
  {
    num: "02",
    name: "February",
    player: "Kawhi Leonard",
    team: "LA Clippers",
    accent: "#C8102E",
    img: "/calendar/02-feb.jpg",
  },
  {
    num: "03",
    name: "March",
    player: "Dwyane Wade",
    team: "Miami Heat",
    accent: "#98002E",
    img: "/calendar/03-mar.jpg",
  },
  {
    num: "04",
    name: "April",
    player: "Chris Webber",
    team: "Sacramento Kings",
    accent: "#5A2D81",
    img: "/calendar/04-apr.jpg",
  },
  {
    num: "05",
    name: "May",
    player: "Kevin Garnett",
    team: "Boston Celtics",
    accent: "#007A33",
    img: "/calendar/05-may.jpg",
  },
  {
    num: "06",
    name: "June",
    player: "LeBron James",
    team: "LA Lakers",
    accent: "#552583",
    img: "/calendar/06-jun.jpg",
  },
  {
    num: "07",
    name: "July",
    player: "Carmelo Anthony",
    team: "New York Knicks",
    accent: "#F58426",
    img: "/calendar/07-jul.jpg",
  },
  {
    num: "08",
    name: "August",
    player: "Kobe Bryant",
    team: "LA Lakers",
    accent: "#FDB927",
    img: "/calendar/08-aug.jpg",
  },
  {
    num: "09",
    name: "September",
    player: "Tony Parker",
    team: "San Antonio Spurs",
    accent: "#C4CED4",
    img: "/calendar/09-sep.jpg",
  },
  {
    num: "10",
    name: "October",
    player: "DeMar DeRozan",
    team: "Toronto Raptors",
    accent: "#CE1141",
    img: "/calendar/10-oct.jpg",
  },
  {
    num: "11",
    name: "November",
    player: "Klay Thompson",
    team: "Golden State Warriors",
    accent: "#FFC72C",
    img: "/calendar/11-nov.jpg",
  },
  {
    num: "12",
    name: "December",
    player: "John Stockton",
    team: "Utah Jazz",
    accent: "#F9A01B",
    img: "/calendar/12-dec.jpg",
  },
];

export default function CalendarSpread() {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const { open } = useLightbox();
  const m = months[idx];

  const go = (next) => {
    const target = (idx + next + months.length) % months.length;
    setDir(next);
    setIdx(target);
  };

  const variants = {
    enter: (d) => ({
      opacity: 0,
      x: d > 0 ? 60 : -60,
      filter: "blur(8px)",
    }),
    center: { opacity: 1, x: 0, filter: "blur(0px)" },
    exit: (d) => ({
      opacity: 0,
      x: d > 0 ? -60 : 60,
      filter: "blur(8px)",
    }),
  };

  return (
    <div
      className="mb-20 md:mb-28"
      data-testid="calendar-spread"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
        <div className="flex items-center gap-4">
          <span className="w-10 h-10 border border-[var(--ink)] flex items-center justify-center">
            <BookOpen className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <h3 className="font-sans-display text-3xl md:text-5xl tracking-[-0.02em] font-light uppercase">
            NBA 2025 · Calendar
          </h3>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
          §03.b — 13-spread photoshop set · click a month
        </span>
      </div>

      {/* Two-page spread */}
      <div
        className="relative bg-[var(--ink)] p-3 md:p-5"
        style={{
          boxShadow: `0 30px 80px -30px ${m.accent}55, 0 10px 30px -10px rgba(0,0,0,0.4)`,
          transition: "box-shadow 700ms ease",
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5 relative">
          {/* Cover — left page */}
          <div
            className="relative aspect-[1348/1906] overflow-hidden bg-black cursor-zoom-in"
            onClick={() => open(COVER, "NBA 2025 calendar cover")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                open(COVER, "NBA 2025 calendar cover");
              }
            }}
          >
            <img
              src={COVER}
              alt="NBA 2025 calendar cover"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <span
              className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/90 bg-black/55 backdrop-blur-sm px-2.5 py-1"
            >
              Cover · Air Jordan tribute
            </span>
            {/* binding shadow on the inside edge */}
            <span
              className="hidden md:block pointer-events-none absolute top-0 right-0 h-full w-12"
              style={{
                background:
                  "linear-gradient(to right, transparent, rgba(0,0,0,0.45))",
              }}
              aria-hidden="true"
            />
          </div>

          {/* Active month — right page */}
          <div
            className="relative aspect-[1348/1906] overflow-hidden bg-black cursor-zoom-in"
            onClick={() => open(m.img, `${m.name} — ${m.player}, ${m.team}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                open(m.img, `${m.name} — ${m.player}, ${m.team}`);
              }
            }}
          >
            <AnimatePresence custom={dir} mode="wait">
              <motion.img
                key={m.num}
                src={m.img}
                alt={`${m.name} — ${m.player}, ${m.team}`}
                custom={dir}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.55, ease: [0.65, 0.05, 0.3, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                data-testid="calendar-active-image"
              />
            </AnimatePresence>
            {/* month tag */}
            <span
              className="absolute top-3 left-3 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-white bg-black/55 backdrop-blur-sm px-2.5 py-1"
              style={{ borderLeft: `3px solid ${m.accent}` }}
            >
              Month · {m.num}
            </span>
            {/* binding shadow on inside edge */}
            <span
              className="hidden md:block pointer-events-none absolute top-0 left-0 h-full w-12"
              style={{
                background:
                  "linear-gradient(to left, transparent, rgba(0,0,0,0.45))",
              }}
              aria-hidden="true"
            />
            {/* Prev / next floating */}
            <div className="absolute bottom-3 right-3 z-10 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  go(-1);
                }}
                data-testid="calendar-prev"
                aria-label="Previous month"
                className="w-9 h-9 border border-white/60 bg-black/55 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  go(1);
                }}
                data-testid="calendar-next"
                aria-label="Next month"
                className="w-9 h-9 border border-white/60 bg-black/55 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Caption strip */}
        <div className="mt-3 md:mt-5 bg-white/[0.06] backdrop-blur-sm border border-white/10 px-5 md:px-7 py-4 md:py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-white">
          <div className="flex items-baseline gap-4">
            <span
              className="font-sans-display text-3xl md:text-4xl leading-none"
              style={{ color: m.accent }}
              data-testid="calendar-month-num"
            >
              {m.num}
            </span>
            <span className="font-display italic text-xl md:text-3xl leading-none">
              {m.name}
            </span>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-right md:text-left">
              <div
                className="font-mono text-[10px] uppercase tracking-[0.3em]"
                style={{ color: m.accent }}
              >
                Featured
              </div>
              <div className="mt-1 font-semibold text-sm md:text-base">
                {m.player}
              </div>
            </div>
            <span className="hidden md:block w-px h-9 bg-white/20" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                Team
              </div>
              <div className="mt-1 font-semibold text-sm md:text-base">
                {m.team}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Month chips */}
      <div className="mt-6 md:mt-8 grid grid-cols-6 md:grid-cols-12 gap-1.5">
        {months.map((mm, i) => {
          const active = i === idx;
          return (
            <button
              key={mm.num}
              onClick={() => {
                setDir(i > idx ? 1 : -1);
                setIdx(i);
              }}
              data-testid={`calendar-chip-${mm.num}`}
              aria-label={`Show ${mm.name}`}
              className="group relative h-12 md:h-14 border border-[var(--ink)] flex flex-col items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
              style={{
                background: active ? mm.accent : "transparent",
                color: active ? "#fff" : "var(--ink)",
                boxShadow: active
                  ? `0 6px 18px -6px ${mm.accent}88`
                  : "none",
              }}
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-70">
                {mm.num}
              </span>
              <span className="font-display italic text-sm leading-none mt-0.5">
                {mm.name.slice(0, 3)}
              </span>
              {/* hover swatch underline */}
              <span
                className="absolute bottom-0 left-0 h-[3px] w-0 group-hover:w-full transition-[width] duration-500"
                style={{ background: mm.accent }}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
